/**
 * Multiplexed WebSocket client.
 *
 * A single persistent connection to ``/ws/stream`` carries events for all
 * conversations.  The connection is opened once after authentication and
 * stays open for the app's lifetime (with auto-reconnect on failures).
 *
 * Use {@link subscribe} when the user opens a conversation so the server
 * attaches the WS to that conversation's running task (if any).
 */

import type { WsClientMessage, WsServerMessage } from "./types";
import { getAccessToken } from "./client";

type MessageHandler = (msg: WsServerMessage) => void;
type StatusHandler = (status: "connecting" | "connected" | "disconnected" | "error" | "session_expired") => void;

// Use the page's own host so the socket works everywhere: the hosted cloud
// (wss://omniwerk.nbyula.com), the dev Vite proxy (localhost:5173), and the
// desktop app (127.0.0.1:19532). Fall back to the loopback default ONLY when
// location.host is empty — some PyWebView builds report it blank. (The old
// "must contain 127.0.0.1" check wrongly forced the loopback on any real
// domain, which broke the WebSocket on the hosted deployment.)
const _host = typeof window !== "undefined" && window.location.host
  ? window.location.host
  : "127.0.0.1:19532";
const WS_BASE = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${_host}`;
const MAX_RECONNECT_DELAY = 10000;

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
let messageHandler: MessageHandler | null = null;
let statusHandler: StatusHandler | null = null;
let _connected = false;
// The most recently subscribed conversation, re-subscribed on every reconnect
// so the server replays its running-state and the "Working" spinner can't wedge.
let lastSubscribed: string | null = null;

function getReconnectDelay(): number {
  return Math.min(1000 * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY);
}

/** Connect to the multiplexed stream.  Call once after auth. */
/** Close any live socket and cancel any pending reconnect.
 *
 *  Every connection attempt goes through this first, so at most ONE socket is
 *  ever attached. That invariant is load-bearing: the backend fans each event
 *  out to every attached socket, and this module has a single handler slot, so
 *  a second socket makes ``messageHandler`` run twice per frame. The damage is
 *  invisible in the id-deduped handlers (``assistant_text``, ``tool_use_start``
 *  both no-op on a repeat) but corrupts the *additive* ones — the live
 *  ``assistant_text_delta`` draft duplicated its text ("Here's theHere's the…")
 *  while the final persisted bubble looked perfect.
 *
 *  Ways a second socket used to appear: React StrictMode double-invoking the
 *  mount effect, an HMR remount, a re-auth flipping ``isAuthenticated``, or two
 *  ``onclose`` handlers each arming a reconnect into the single timer slot
 *  (the overwritten timer still fires).
 */
function _teardownSocket() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    ws.onclose = null;    // our own teardown must not trigger a reconnect
    ws.onmessage = null;  // an in-flight frame must not reach the handler
    ws.close();
    ws = null;
  }
}

export function connect(
  onMessage: MessageHandler,
  onStatus: StatusHandler,
) {
  _teardownSocket();
  messageHandler = onMessage;
  statusHandler = onStatus;
  reconnectAttempts = 0;
  _connected = false;
  _doConnect();
}

function _doConnect() {
  // Self-guarding: also covers the reconnect-timer entry point, so a racing
  // timer can never stack a second socket on top of a live one.
  _teardownSocket();
  const token = getAccessToken();
  if (!token) {
    statusHandler?.("error");
    return;
  }

  statusHandler?.("connecting");

  const url = `${WS_BASE}/ws/stream?token=${encodeURIComponent(token)}`;
  ws = new WebSocket(url);

  ws.onopen = () => {
    reconnectAttempts = 0;
    _connected = true;
    statusHandler?.("connected");
    // Re-subscribe to the active conversation on EVERY (re)connect so the
    // server replays its current running-state (a ``task_status`` frame).
    // Without this, a window that reconnects after missing a turn's clearing
    // event keeps a stale "Working" spinner until a full reload — most visibly
    // for a turn run on ANOTHER env (portable sessions): its clearing
    // ``conversation_upsert{agent_running:0}`` is a best-effort per-socket
    // broadcast that's dropped while this socket is down, and nothing re-sends
    // it (later pulls re-fetch the unchanged row, which fails the "changed"
    // gate). The subscribe reply reconciles it from the durable row.
    if (lastSubscribed) {
      send({ type: "subscribe", conversation_id: lastSubscribed });
    }
    // Deliver anything that raced the closed socket — AFTER the subscribe so
    // replies to these sends stream to a subscribed window.
    flushPendingSends();
  };

  ws.onmessage = (event) => {
    try {
      const msg: WsServerMessage = JSON.parse(event.data);
      messageHandler?.(msg);
    } catch (e) {
      console.error("Failed to parse WS message:", e);
    }
  };

  ws.onclose = (event) => {
    ws = null;
    _connected = false;

    // Auth failure or forced logout — surface as session_expired so the UI
    // can show a non-dismissable re-login prompt instead of "reconnecting…"
    if (event.code === 4001 || event.code === 4003) {
      statusHandler?.("session_expired");
      return;
    }

    statusHandler?.("disconnected");

    // Auto-reconnect with exponential backoff
    if (messageHandler) {
      reconnectAttempts++;
      const delay = getReconnectDelay();
      reconnectTimer = setTimeout(_doConnect, delay);
    }
  };

  ws.onerror = () => {
    statusHandler?.("error");
  };
}

/** Sends that raced a closed/connecting socket, delivered on the next open.
 *  Dropping them silently loses USER MESSAGES: the optimistic bubble renders,
 *  nothing reaches the server, and the chat looks like it ignored the send
 *  (2026-08-04). Bounded and age-capped so a long outage can't replay a
 *  stale burst. */
const pendingSends: { msg: WsClientMessage; at: number }[] = [];
const PENDING_SEND_MAX = 50;
const PENDING_SEND_TTL_MS = 120_000;

function flushPendingSends() {
  if (ws?.readyState !== WebSocket.OPEN) return;
  const now = Date.now();
  for (const p of pendingSends.splice(0)) {
    if (now - p.at <= PENDING_SEND_TTL_MS) {
      ws.send(JSON.stringify(p.msg));
    } else {
      console.warn("Dropping stale buffered send:", p.msg.type);
    }
  }
}

/** Send a message to the server (buffered across reconnects). */
export function send(msg: WsClientMessage) {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(msg));
  } else {
    console.warn("WebSocket not connected — buffering send:", msg.type);
    pendingSends.push({ msg, at: Date.now() });
    if (pendingSends.length > PENDING_SEND_MAX) pendingSends.shift();
  }
}

/** Subscribe to a conversation's live events. Remembered so the socket can
 *  re-subscribe on reconnect (see ``onopen``). */
export function subscribe(conversationId: string) {
  lastSubscribed = conversationId;
  send({ type: "subscribe", conversation_id: conversationId });
}

/** Disconnect entirely (logout). */
export function disconnect() {
  // Clear the handlers FIRST so the teardown can't hand a late frame or status
  // to a logged-out app; ``_teardownSocket`` then closes the socket without
  // arming a reconnect.
  messageHandler = null;
  statusHandler = null;
  _connected = false;
  _teardownSocket();
}

/** Check if currently connected. */
export function isConnected(): boolean {
  return _connected && ws?.readyState === WebSocket.OPEN;
}
